import { createSlice, PayloadAction } from "@reduxjs/toolkit";

/**
 * Instance history entry interface
 */
export interface IInstanceHistory {
  action: string;
  initiatedBy: string;
  initiatedById: string;
  status: string;
  startTime: string;
  endTime: string;
}

/**
 * Instance history page data interface
 */
export interface IInstanceHistoryPageData {
  instanceId: string;
  instanceName: string;
  instanceHistory: IInstanceHistory[];
}

/**
 * Instance history state interface
 */
export interface InstanceHistoryState {
  instanceHistoryPageData: IInstanceHistoryPageData | null;
}

const initialState: InstanceHistoryState = {
  instanceHistoryPageData: null,
};

/**
 * Instance History slice for managing instance execution history
 *
 * This slice manages the history of instance operations including
 * actions, initiators, status, and timestamps.
 *
 * @example
 * ```typescript
 * // Save instance history data
 * dispatch(saveInstanceHistory({
 *   instanceId: 'inst-123',
 *   instanceName: 'Production Instance',
 *   instanceHistory: [
 *     {
 *       action: 'Start',
 *       initiatedBy: 'John Doe',
 *       initiatedById: 'user-456',
 *       status: 'Completed',
 *       startTime: '2025-01-01T10:00:00Z',
 *       endTime: '2025-01-01T10:05:00Z'
 *     }
 *   ]
 * }));
 *
 * // Clear instance history
 * dispatch(clearInstanceHistory());
 * ```
 */
const instanceHistorySlice = createSlice({
  name: "instanceHistory",
  initialState,
  reducers: {
    /**
     * Save instance history page data
     * @param state - Current instance history state
     * @param action - Payload containing instance history data
     */
    saveInstanceHistory: (state, action: PayloadAction<IInstanceHistoryPageData>) => {
      state.instanceHistoryPageData = action.payload;
    },

    /**
     * Update a specific history entry
     * @param state - Current instance history state
     * @param action - Payload containing the index and updated history entry
     */
    updateHistoryEntry: (
      state,
      action: PayloadAction<{ index: number; entry: IInstanceHistory }>,
    ) => {
      if (state.instanceHistoryPageData?.instanceHistory) {
        state.instanceHistoryPageData.instanceHistory[action.payload.index] = action.payload.entry;
      }
    },

    /**
     * Add a new history entry
     * @param state - Current instance history state
     * @param action - Payload containing the new history entry
     */
    addHistoryEntry: (state, action: PayloadAction<IInstanceHistory>) => {
      if (state.instanceHistoryPageData?.instanceHistory) {
        state.instanceHistoryPageData.instanceHistory.unshift(action.payload);
      }
    },

    /**
     * Clear instance history data
     * @param state - Current instance history state
     */
    clearInstanceHistory: (state) => {
      state.instanceHistoryPageData = null;
    },
  },
});

export const { saveInstanceHistory } = instanceHistorySlice.actions;

export default instanceHistorySlice.reducer;
